/**
 * The navigation column: who you are, and every conversation you are in.
 *
 * Direct messages have no name of their own — the contract leaves `name` null
 * for them — so they are resolved from the membership and published to a small
 * shared registry. That is what lets the overview, the room switcher and the
 * Alt+Arrow ordering in `AppShell` talk about a direct message by the name the
 * user actually sees, without every one of them being fetched twice. The
 * registry is written by `DirectRoomLabels`, which the shell mounts whether or
 * not this column is on screen.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react'
import type { ReactElement } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { useAuth } from '@/api/auth'
import { api } from '@/api/client'
import { useMembers } from '@/api/queries'
import { flushCachedChanges, refreshCachedRoom, usePresence, useSyncStore } from '@/api/store'
import type { PresenceEntry } from '@/api/store'
import { activityCount, isMutedRoom, unreadOf } from '@/api/types'
import type { Member, Room, RoomKind, User } from '@/api/types'
import { Avatar, Badge, HashIcon, LockIcon, SpaceLogo, Tooltip, useToast } from '@/components/ui'
import { AccountMenu } from '@/components/AccountMenu'
import { ChannelBrowser } from '@/components/ChannelBrowser'
import { NewRoomDialog } from '@/components/NewRoomDialog'
import { useContainedScroll } from '@/hooks/useContainedScroll'
import styles from './Sidebar.module.css'

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter((value): value is string => typeof value === 'string' && value !== '').join(' ')
}

/** A person's best available name. `display_name` can arrive empty. */
export function personName(user: User): string {
  const display = user.display_name.trim()
  return display === '' ? user.username : display
}

// ── The label registry ──────────────────────────────────────────────────────

const labelValues = new Map<string, string>()
const labelListeners = new Set<() => void>()
let labelSnapshot: ReadonlyMap<string, string> = new Map<string, string>()

function publishRoomLabel(roomId: string, label: string): void {
  if (labelValues.get(roomId) === label) return
  labelValues.set(roomId, label)
  // A fresh map each time, so `useSyncExternalStore` sees a changed snapshot.
  labelSnapshot = new Map(labelValues)
  for (const listener of labelListeners) listener()
}

function subscribeToRoomLabels(listener: () => void): () => void {
  labelListeners.add(listener)
  return () => {
    labelListeners.delete(listener)
  }
}

function readRoomLabels(): ReadonlyMap<string, string> {
  return labelSnapshot
}

/** Labels for rooms the sidebar has rendered. Empty for anything it has not. */
export function useRoomLabels(): ReadonlyMap<string, string> {
  return useSyncExternalStore(subscribeToRoomLabels, readRoomLabels, readRoomLabels)
}

/**
 * Resolves every direct room's name, whether or not the sidebar is on screen.
 *
 * The labels used to be a by-product of drawing a sidebar row. On a phone — and
 * on an iPad sharing its screen — the sidebar is a closed drawer that renders
 * nothing, so the overview and the room switcher called every direct message
 * "Direct message" and drew "DM" in place of the other person's initials. These
 * probes are the same membership fetch, mounted by the shell and drawing
 * nothing themselves; React Query hands the sidebar rows the same response.
 */
export function DirectRoomLabels({
  rooms,
  selfId,
}: {
  rooms: readonly Room[]
  selfId: string | null
}): ReactElement {
  return (
    <>
      {rooms
        .filter((room) => room.kind !== 'channel')
        .map((room) => (
          <DirectRoomLabel key={room.id} room={room} selfId={selfId} />
        ))}
    </>
  )
}

function DirectRoomLabel({ room, selfId }: { room: Room; selfId: string | null }): null {
  const { data: members } = useMembers(room.id)
  const label = useMemo(
    () => directLabel(room, members, selfId),
    [room, members, selfId],
  )

  useEffect(() => {
    publishRoomLabel(room.id, label)
  }, [room.id, label])

  return null
}

/** A direct room is called after whoever else is in it. */
function directLabel(
  room: Room,
  members: readonly Member[] | undefined,
  selfId: string | null,
): string {
  if (members === undefined) return room.name ?? 'Direct message'
  const participants = directParticipants(members, selfId)
  if (participants.length === 0) return room.name ?? 'Direct message'
  return participants.map((member) => personName(member.user)).join(', ')
}

/**
 * Forgets every label. Called when the account changes or signs out: a direct
 * message is labelled with the *other* participant's name, and the same room
 * seen from the next account has a different other.
 */
export function resetRoomLabels(): void {
  if (labelValues.size === 0) return
  labelValues.clear()
  labelSnapshot = new Map<string, string>()
  for (const listener of labelListeners) listener()
}

export function roomLabel(room: Room, labels: ReadonlyMap<string, string>): string {
  if (room.kind === 'channel') return room.name ?? room.slug ?? 'Channel'
  return labels.get(room.id) ?? room.name ?? 'Direct message'
}

function directParticipants(members: readonly Member[], selfId: string | null): Member[] {
  const others = members.filter((member) => member.user.id !== selfId)
  // A note-to-self room has no "other", and must still be nameable.
  return others.length > 0 ? others : [...members]
}

// ── Ordering ────────────────────────────────────────────────────────────────

export interface GroupedRooms {
  directs: Room[]
  channels: Room[]
}

/**
 * Alphabetical within each group, archived rooms last. Deliberately not
 * ordered by activity: a list that reshuffles itself under the pointer is
 * hostile to muscle memory, and unread state is already shown on the row.
 */
export function groupRooms(rooms: readonly Room[], labels: ReadonlyMap<string, string>): GroupedRooms {
  const directs: Room[] = []
  const channels: Room[] = []

  for (const room of rooms) {
    if (room.kind === 'channel') channels.push(room)
    else directs.push(room)
  }

  const compare = (left: Room, right: Room): number => {
    const archived = Number(left.archived_at !== null) - Number(right.archived_at !== null)
    if (archived !== 0) return archived
    return roomLabel(left, labels).localeCompare(roomLabel(right, labels))
  }

  directs.sort(compare)
  channels.sort(compare)
  return { directs, channels }
}

/** How a shared room is explained on hover, from whichever side you are on. */
export function sharingSummary(room: Room): string | null {
  if (!room.is_shared && room.is_local) return null

  if (!room.is_local) return `Shared from ${room.owner_server_id}`

  const partners = room.shared_with ?? []
  if (partners.length === 0) return 'Shared with another server'
  return `Shared with ${partners.join(', ')}`
}

// ── Icons ───────────────────────────────────────────────────────────────────

function PlusIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" focusable="false">
      <path d="M8 3.5v9M3.5 8h9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function SearchIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="14" height="14" fill="none" aria-hidden="true" focusable="false">
      <circle cx="7.2" cy="7.2" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path d="m10.4 10.4 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function ActivityIcon(): ReactElement {
  return (
    <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true" focusable="false">
      <path d="M8 3.2V8l3 1.8" stroke="currentColor" strokeWidth="1.45" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.45" />
    </svg>
  )
}

// ── Rows ────────────────────────────────────────────────────────────────────

interface RoomRowProps {
  room: Room
  selfId: string | null
  presence: ReadonlyMap<string, PresenceEntry>
  onNavigate: () => void
}

function RoomRow({ room, selfId, presence, onNavigate }: RoomRowProps): ReactElement {
  const toast = useToast()
  const [savingStar, setSavingStar] = useState(false)
  const isDirect = room.kind !== 'channel'
  // Only direct rooms need their membership to be nameable; channels have a name.
  const { data: members } = useMembers(isDirect ? room.id : undefined)

  const participants = useMemo(
    () => (isDirect && members !== undefined ? directParticipants(members, selfId) : []),
    [isDirect, members, selfId],
  )

  const label = useMemo(
    () => (isDirect ? directLabel(room, members, selfId) : room.name ?? room.slug ?? 'Channel'),
    [isDirect, room, members, selfId],
  )

  // A closed channel keeps the hash in the name because the padlock has
  // taken the icon's place. Open channels keep a bare name next to `#`.
  const displayLabel =
    !isDirect && room.visibility === 'private' ? `#${label}` : label
  const muted = isMutedRoom(room)
  const counterpart = room.kind === 'direct' ? participants[0]?.user : undefined
  const isSelfDirect = room.kind === 'direct' && counterpart?.id === selfId
  const online = counterpart === undefined ? null : presence.get(counterpart.id)?.status === 'online'

  const sharing = sharingSummary(room)
  const unreadCount = unreadOf(room).count
  const mentions = unreadOf(room).mentions

  const glyph =
    counterpart !== undefined ? (
      <Avatar
        name={label}
        id={counterpart.id}
        src={counterpart.avatar_url}
        size="xs"
        presence={online === null ? null : online ? 'online' : 'offline'}
        decorative
      />
    ) : isDirect ? (
      <Avatar name={label} id={room.id} size="xs" decorative />
    ) : room.visibility === 'private' ? (
      <LockIcon />
    ) : (
      <HashIcon />
    )

  const toggleStar = async (): Promise<void> => {
    const membership = room.my_membership
    if (membership?.id === undefined || savingStar) return
    const starred = membership.starred !== true
    setSavingStar(true)
    try {
      await api.patch(`/rooms/${encodeURIComponent(room.id)}/members/${encodeURIComponent(membership.id)}`, {
        starred,
      })
      refreshCachedRoom({ ...room, my_membership: { ...membership, starred } })
      // A reload resumes from IndexedDB and incremental sync intentionally
      // carries no full room list. Land this personal preference before the
      // button becomes active again, so a quick reload cannot resurrect the
      // previous sidebar order.
      await flushCachedChanges()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'The conversation could not be updated.')
    } finally {
      setSavingStar(false)
    }
  }

  return (
    <li className={styles.roomItem}>
      <NavLink
        to={`/rooms/${room.id}`}
        onClick={onNavigate}
        className={({ isActive }) =>
          cx(
            styles.row,
            isActive && styles.active,
            unreadCount > 0 && !muted && styles.unread,
            muted && styles.muted,
            room.archived_at !== null && styles.archived,
          )
        }
      >
        <span className={styles.glyph}>{glyph}</span>
        <span className={styles.name}>{displayLabel}</span>

        {isSelfDirect ? <span className={styles.you}>you</span> : null}

        {sharing !== null ? (
          <Tooltip label={sharing} placement="right">
            <span className={styles.shared} role="img" aria-label={sharing}>
              ↔
            </span>
          </Tooltip>
        ) : null}

        {muted ? null : mentions > 0 ? (
          <Badge tone="info" emphasis="solid">
            <span aria-hidden="true">@{mentions}</span>
            <span className="sr-only">{`${mentions} mentions`}</span>
          </Badge>
        ) : unreadCount > 0 ? (
          <span className={styles.count}>
            <span aria-hidden="true">{unreadCount > 99 ? '99+' : unreadCount}</span>
            <span className="sr-only">{`${unreadCount} unread`}</span>
          </span>
        ) : null}
      </NavLink>
      {room.my_membership?.id !== undefined ? (
        <button
          type="button"
          className={cx(styles.star, room.my_membership.starred === true && styles.starred)}
          aria-label={room.my_membership.starred === true ? `Unstar ${label}` : `Star ${label}`}
          aria-pressed={room.my_membership.starred === true}
          disabled={savingStar}
          onClick={() => void toggleStar()}
        >
          <span aria-hidden="true">{room.my_membership.starred === true ? '★' : '☆'}</span>
        </button>
      ) : null}
    </li>
  )
}

interface RoomGroupProps {
  title: string
  rooms: readonly Room[]
  emptyLabel: string
  actionLabel?: string
  selfId: string | null
  presence: ReadonlyMap<string, PresenceEntry>
  onAction?: () => void
  onNavigate: () => void
}

function RoomGroup({
  title,
  rooms,
  emptyLabel,
  actionLabel,
  selfId,
  presence,
  onAction,
  onNavigate,
}: RoomGroupProps): ReactElement {
  const headingId = `sidebar-group-${title.replace(/\s+/gu, '-').toLowerCase()}`

  return (
    <section className={styles.group} aria-labelledby={headingId}>
      <div className={styles.groupHeader}>
        <h2 className={styles.groupTitle} id={headingId}>
          {title === 'Favorites' ? (
            <span className={styles.starredMark} aria-hidden="true">
              ★
            </span>
          ) : null}
          {title}
        </h2>
        {actionLabel !== undefined && onAction !== undefined ? (
          <button
            type="button"
            className={styles.groupAction}
            aria-label={title === 'Direct messages' ? 'New message' : actionLabel}
            onClick={onAction}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>

      {rooms.length === 0 ? (
        <p className={styles.emptyGroup}>{emptyLabel}</p>
      ) : (
        <ul className={styles.list}>
          {rooms.map((room) => (
            <RoomRow
              key={room.id}
              room={room}
              selfId={selfId}
              presence={presence}
              onNavigate={onNavigate}
            />
          ))}
        </ul>
      )}
    </section>
  )
}

// ── Sidebar ─────────────────────────────────────────────────────────────────

export interface SidebarProps {
  /** Called after a room is chosen, so a slide-over can close itself. */
  onNavigate?: () => void
  /** Opens the room switcher owned by the shell. */
  onOpenSwitcher?: () => void
  className?: string
}

export function Sidebar({ onNavigate, onOpenSwitcher, className }: SidebarProps): ReactElement {
  const { user, server } = useAuth()
  const location = useLocation()
  const { rooms } = useSyncStore()
  const presence = usePresence()
  const labels = useRoomLabels()
  const [creating, setCreating] = useState<RoomKind | null>(null)
  const [browsing, setBrowsing] = useState(false)
  const scrollRef = useRef<HTMLElement>(null)
  useContainedScroll(scrollRef)

  const { directs, channels } = useMemo(() => groupRooms(rooms, labels), [rooms, labels])
  const yourChannels = useMemo(
    () => channels.filter((room) => room.my_membership != null),
    [channels],
  )
  // Every channel this reader can reach: the open ones joined or not, and the
  // ones they are a member of, a private channel included. The browser marks
  // the ones you are already in rather than hiding them, and a channel you
  // were added to that the search cannot find is, to the person looking, a
  // channel that does not exist.
  const browsableChannels = useMemo(
    () =>
      channels.filter(
        (room) =>
          room.my_membership != null ||
          (room.visibility === 'public' && room.archived_at === null),
      ),
    [channels],
  )
  const starred = useMemo(
    () => rooms.filter((room) => room.my_membership?.starred === true),
    [rooms],
  )
  const unstarredDirects = useMemo(
    () => directs.filter((room) => room.my_membership?.starred !== true),
    [directs],
  )
  const unstarredChannels = useMemo(
    () => yourChannels.filter((room) => room.my_membership?.starred !== true),
    [yourChannels],
  )
  // The same rooms the title and the Overview count: joined, and not muted.
  const activityTotal = useMemo(() => activityCount(rooms), [rooms])

  const workspaceName = useMemo(() => {
    const configured = server?.display_name?.trim() ?? ''
    if (configured !== '') return configured
    return server?.server_id ?? 'relaii'
  }, [server])

  const handleNavigate = useCallback(() => {
    onNavigate?.()
  }, [onNavigate])

  const closeDialog = useCallback(() => setCreating(null), [])

  return (
    <div className={cx(styles.sidebar, className)}>
      <div className={styles.profileHeader}>
        <div className={styles.workspace}>
          <SpaceLogo src={server?.logo_url ?? null} name={workspaceName} size="md" decorative />
          <span className={styles.workspaceIdentity}><span className={styles.workspaceName} title={workspaceName}>{workspaceName}</span>
            <span className={styles.workspaceServer}>{server?.server_id ?? ''}</span></span>
        </div>
        <div className={styles.headerActions}>
          {onOpenSwitcher !== undefined ? (
            <button
              className={styles.searchControl}
              type="button"
              aria-label="Find a room"
              title="Find a room"
              onClick={onOpenSwitcher}
            >
              <SearchIcon />
              <span className={styles.searchLabel}>Search</span>
              <span className={styles.searchShortcut} aria-hidden="true">
                ⌘K
              </span>
            </button>
          ) : null}
          <button type="button" className={styles.searchControl} aria-label="New channel" onClick={() => setCreating('channel')}>
            <PlusIcon /><span>New</span>
          </button>
        </div>
      </div>

      <nav ref={scrollRef} className={styles.scroll} aria-label="Conversations">
        {(['Overview', 'Activity', 'Saved', 'Files'] as const).map((section) => {
          const path = section === 'Overview' ? '/overview' : `/overview/${section.toLowerCase()}`
          const active = location.pathname === path
          return <NavLink key={section} to={path} end onClick={handleNavigate}
            className={cx(styles.activity, active && styles.activityActive)}>
            <span className={styles.activityIcon} aria-hidden="true">{section === 'Activity' ? <ActivityIcon /> : section === 'Overview' ? '▦' : section === 'Saved' ? '☆' : '↗'}</span>
            <span className={styles.activityName}>{section}</span>
            {section === 'Activity' && activityTotal > 0 ? <span className={styles.count}>
              <span aria-hidden="true">{activityTotal > 99 ? '99+' : activityTotal}</span>
              <span className="sr-only">{`${activityTotal} unread activities`}</span>
            </span> : null}
          </NavLink>
        })}
        {starred.length > 0 ? (
          <RoomGroup
            title="Favorites"
            rooms={starred}
            emptyLabel=""
            selfId={user?.id ?? null}
            presence={presence}
            onNavigate={handleNavigate}
          />
        ) : null}
        <RoomGroup
          title="Rooms"
          rooms={unstarredChannels}
          emptyLabel="You have not joined a channel yet."
          actionLabel="Browse"
          selfId={user?.id ?? null}
          presence={presence}
          onAction={() => setBrowsing(true)}
          onNavigate={handleNavigate}
        />
        <RoomGroup
          title="Direct messages"
          rooms={unstarredDirects}
          emptyLabel="No conversations yet."
          actionLabel="New"
          selfId={user?.id ?? null}
          presence={presence}
          onAction={() => setCreating('direct')}
          onNavigate={handleNavigate}
        />
      </nav>

      <div className={styles.profileFooter}><AccountMenu onNavigate={handleNavigate} /></div>

      <NewRoomDialog
        open={creating !== null}
        kind={creating ?? 'channel'}
        onClose={closeDialog}
        onCreated={handleNavigate}
      />
      <ChannelBrowser
        open={browsing}
        channels={browsableChannels}
        onClose={() => setBrowsing(false)}
        onNavigate={handleNavigate}
      />
    </div>
  )
}
